import type { Middleware } from '@arcubase/client-ts'
import { requiresTenantContext } from '@arcubase/client-ts/src/api-meta'
import { isArray } from '@/utils'

// 认证中间件 (token/tenant 已由 interceptor 自动注入，这里只做预验证)
export const authMiddleware: Middleware = async (context, next) => {
  const { config } = context

  // 自动检查租户上下文（基于 routes.go 元信息）
  const needsTenant = requiresTenantContext(config.method || 'GET', config.url || '')
  
  if (needsTenant) {
    const hasTenantHeader = config.headers?.['X-Tenant-Id']
    if (!hasTenantHeader) {
      // 租户上下文缺失，提前拦截
      return Promise.reject({
        type: 'user',
        code: 'MISSING_TENANT',
        message: `Tenant context required for ${config.method?.toUpperCase()} ${config.url}`
      })
    }
  }

  return await next()
}

// 表单数据处理中间件
export const formDataMiddleware: Middleware = async (context, next) => {
  const { config } = context
  
  // 处理 multipart/form-data
  if (config.headers?.['Content-Type'] === 'multipart/form-data' && config.data) {
    const form = new FormData()
    Object.keys(config.data).forEach(key => {
      if (isArray(config.data[key])) {
        config.data[key].forEach((item: any) => {
          form.append(key, item)
        })
      } else {
        form.append(key, config.data[key])
      }
    })
    config.data = form
  }
  
  return await next()
}

// 版本检查中间件
export const versionCheckMiddleware: Middleware = async (context, next) => {
  const result = await next()
  
  // === RESPONSE 阶段 (next() 之后) ===
  const { response } = context
  
  if (response?.status === 200 && response.data?.build) {
    // 版本检查
    if (window.CURRENT_VERSION && typeof window.CURRENT_VERSION === 'object') {
      if (window.CURRENT_VERSION.value !== response.data.build) {
        window.CURRENT_VERSION.value = response.data.build
      }
    }
  }
  
  return result
}

// 数据转换中间件
export const dataTransformMiddleware: Middleware = async (context, next) => {
  const result = await next()
  
  // === RESPONSE 阶段 (next() 之后) ===
  const { response } = context
  
  // 为响应数据添加 meta 方法
  if (response?.data?.data && typeof response.data.data === 'object') {
    response.data.data.meta = () => response.data.meta
  }
  
  return result
}

// 请求日志中间件 (开发环境)
export const requestLogMiddleware: Middleware = async (context, next) => {
  const { config } = context
  const startTime = Date.now()
  
  // 在 context 中存储开始时间，供响应阶段使用
  context.startTime = startTime
  
  try {
    const result = await next()
    
    return result
  } catch (error) {
    // === ERROR 阶段 ===
    if (process.env.NODE_ENV === 'development') {
      const duration = Date.now() - startTime
      console.log(`❌ Error: ${config.method?.toUpperCase()} ${config.url} (${duration}ms)`, error)
    }
    throw error
  }
}
